import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import { useUser, useLogout } from '@/hooks/auth';
import { useTranslations, useLocale, type Locale, LANGUAGE_SWITCHING_ENABLED } from '@/i18n';
import { cn } from '@/lib/utils';

interface AvatarCircleProps {
  avatarUrl: string | null;
  fallbackText?: string;
  className?: string;
  onError?: () => void;
}

function AvatarCircle({ avatarUrl, fallbackText, className, onError }: AvatarCircleProps) {
  const initials = useMemo(() => {
    if (!fallbackText) {
      return 'U';
    }
    const parts = fallbackText
      .split(' ')
      .filter((part) => part.length > 0)
      .slice(0, 2);
    if (!parts.length) {
      return 'U';
    }
    return parts
      .map((part) => part[0]!.toUpperCase())
      .join('')
      .slice(0, 2);
  }, [fallbackText]);

  return (
    <div
      className={cn(
        'overflow-hidden rounded-full bg-gray-300',
        avatarUrl ? '' : 'bg-gradient-to-br from-[#667eea] to-[#764ba2]',
        className
      )}
    >
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={fallbackText || 'Profile'}
          className="h-full w-full object-cover"
          onError={onError}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-sm font-medium text-white">
          {initials}
        </div>
      )}
    </div>
  );
}

interface UserProfileDropdownProps {
  className?: string;
}

const LANGUAGE_OPTIONS: { value: Locale; label: string }[] = [
  { value: 'ka', label: 'ქართული' },
  { value: 'en', label: 'English' },
];

export default function UserProfileDropdown({ className = '' }: UserProfileDropdownProps) {
  const { data: user } = useUser();
  const { mutate: logout, isPending: isLoggingOut } = useLogout();
  const [imageError, setImageError] = useState(false);
  const t = useTranslations('auth.user');
  const tLang = useTranslations('language');
  const { locale, setLocale } = useLocale();
  const queryClient = useQueryClient();
  const avatarUrl = user?.avatarUrl?.trim() || null;

  const handleLocaleChange = (newLocale: Locale) => {
    if (newLocale === locale) return;
    setLocale(newLocale);
    queryClient.invalidateQueries();
  };

  useEffect(() => {
    setImageError(false);
  }, [avatarUrl]);

  const handleLogout = () => {
    logout();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`flex items-center justify-center transition-all duration-200 hover:scale-105 ${className}`}
          aria-label="User profile menu"
        >
          <AvatarCircle
            avatarUrl={imageError ? null : avatarUrl}
            fallbackText={user?.name}
            className="h-[32px] w-[32px] cursor-pointer md:h-[39px] md:w-[39px]"
            onError={() => setImageError(true)}
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-[280px] rounded-xl border border-[#eeeff0] bg-white p-0 shadow-[0px_8px_24px_-8px_rgba(31,31,31,0.12)]"
      >
        <div className="flex flex-col">
          {/* User Info Section */}
          <div className="border-b border-[#f1f1f1] px-4 py-4">
            <div className="flex items-center gap-3">
              <AvatarCircle
                avatarUrl={imageError ? null : avatarUrl}
                fallbackText={user?.name}
                className="h-10 w-10 shrink-0"
                onError={() => setImageError(true)}
              />
              <div className="flex min-w-0 flex-1 flex-col">
                <p className="truncate text-sm font-medium text-black">
                  {user?.name || t('defaultName')}
                </p>
                <p className="truncate text-xs text-gray-500">{user?.email || ''}</p>
              </div>
            </div>
          </div>

          {/* Language Switcher */}
          {LANGUAGE_SWITCHING_ENABLED && (
            <div className="border-b border-[#f1f1f1] px-4 py-3">
              <p className="mb-2 text-xs font-medium text-gray-500">{tLang('label')}</p>
              <div className="flex gap-1">
                {LANGUAGE_OPTIONS.map((option) => (
                  <Button
                    key={option.value}
                    onClick={() => handleLocaleChange(option.value)}
                    variant="unstyled"
                    size="none"
                    className={cn(
                      'flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      locale === option.value
                        ? 'bg-[#f0f3ff] text-[#4f46e5]'
                        : 'text-gray-600 hover:bg-gray-100'
                    )}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Menu Items */}
          <div className="py-2">
            {/* Logout Button */}
            <Button
              onClick={handleLogout}
              disabled={isLoggingOut}
              variant="unstyled"
              size="none"
              className="inline-flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left text-sm text-red-600 transition-colors hover:bg-red-50"
            >
              {isLoggingOut ? (
                <Spinner variant="circle" size={16} className="text-red-600" />
              ) : (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
              )}
              {isLoggingOut ? t('loggingOut') : t('logout')}
            </Button>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
